import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Button, Icon } from 'semantic-ui-react';
import { applyServiceWorkerUpdate } from '../serviceWorkerRegistration';
import {
  getAppUpdateKey,
  isAppUpdateAvailable,
  normalizeAppVersion,
} from '../utils/appVersion';

const VersionUpdateNotifier = () => {
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [newVersion, setNewVersion] = useState(null);
  const [newBuildId, setNewBuildId] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [improvements, setImprovements] = useState([]);
  const [isChecking, setIsChecking] = useState(false);
  const [checkCount, setCheckCount] = useState(0);
  const MAX_CHECKS = 3;

  // Store the user's choice in localStorage to persist across reloads
  const getUserChoice = useCallback(() => {
    const choice = localStorage.getItem('versionUpdateChoice');
    if (choice) {
      try {
        const { version, timestamp } = JSON.parse(choice);
        // If the choice was made less than 24 hours ago, respect it
        if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
          return version;
        }
      } catch (error) {
        console.warn('Invalid saved versionUpdateChoice value, ignoring.', error);
      }
    }
    return null;
  }, []);

  const saveUserChoice = useCallback((version) => {
    localStorage.setItem('versionUpdateChoice', JSON.stringify({
      version,
      timestamp: Date.now()
    }));
  }, []);

  const checkForUpdates = useCallback(async () => {
    if (isChecking || checkCount >= MAX_CHECKS) return;
    setIsChecking(true);
    setCheckCount(prev => prev + 1);

    try {
      const registration = await navigator.serviceWorker.ready;
      if (registration.active) {
        registration.active.postMessage({ type: 'CHECK_VERSION' });
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
    } finally {
      setIsChecking(false);
    }
  }, [isChecking, checkCount]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // Don't check immediately after a reload from update
    const lastUpdate = localStorage.getItem('lastUpdateReload');
    const timeSinceUpdate = lastUpdate ? Date.now() - parseInt(lastUpdate) : Infinity;

    // If we just reloaded from an update (less than 10 seconds ago), skip checking
    if (timeSinceUpdate < 10000) {
      console.log('Recently updated, skipping version check');
      return;
    }

    // Check for updates when component mounts with a delay
    const initialCheck = setTimeout(() => {
      checkForUpdates();
    }, 5000);

    // Listen for version update messages from service worker
    const handleMessage = (event) => {
      if (event.data?.type === 'NEW_VERSION_AVAILABLE') {
        const incomingNewVersion = normalizeAppVersion(event.data.version);
        const incomingCurrentVersion = normalizeAppVersion(event.data.currentVersion);
        const incomingBuildId = normalizeAppVersion(event.data.buildId);
        const incomingCurrentBuildId = normalizeAppVersion(event.data.currentBuildId);

        if (!isAppUpdateAvailable({
          availableVersion: incomingNewVersion,
          currentVersion: incomingCurrentVersion,
          availableBuildId: incomingBuildId,
          currentBuildId: incomingCurrentBuildId,
        })) {
          return;
        }

        const userChoice = getUserChoice();
        const incomingUpdateKey = getAppUpdateKey(incomingNewVersion, incomingBuildId);
        // Only show modal if user hasn't made a choice for this version
        if (!userChoice || userChoice !== incomingUpdateKey) {
          const incomingImprovements = Array.isArray(event.data.improvements)
            ? event.data.improvements.filter((item) => typeof item === 'string' && item.trim().length > 0)
            : [];

          setNewVersion(incomingNewVersion);
          setNewBuildId(incomingBuildId || null);
          setCurrentVersion(incomingCurrentVersion);
          setImprovements(incomingImprovements);
          setShowUpdateModal(true);
          void applyServiceWorkerUpdate();
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);

    return () => {
      clearTimeout(initialCheck);
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [checkForUpdates, getUserChoice]);

  const handleUpdate = async () => {
    saveUserChoice(getAppUpdateKey(newVersion, newBuildId));
    setShowUpdateModal(false);

    // Mark that we're about to reload from an update
    localStorage.setItem('lastUpdateReload', Date.now().toString());

    const activationStarted = await applyServiceWorkerUpdate();
    if (!activationStarted) {
      window.location.reload();
    }
  };

  const handleClose = () => {
    saveUserChoice(getAppUpdateKey(newVersion, newBuildId));
    setShowUpdateModal(false);
  };

  return (
    <Modal
      open={showUpdateModal}
      onClose={handleClose}
      size="small"
      closeIcon
    >
      <Modal.Header>
        <Icon name="info circle" color="blue" />
        New Version Available
      </Modal.Header>
      <Modal.Content>
        <p>A new version of QR Maps is available!</p>
        <p>Current version: {currentVersion}</p>
        <p>New version: {newVersion}</p>
        {improvements.length > 0 ? (
          <>
            <p>What&apos;s new:</p>
            <ul style={{ marginTop: 0 }}>
              {improvements.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </>
        ) : null}
        <p>Please update to get the latest features and improvements.</p>
      </Modal.Content>
      <Modal.Actions>
        <Button negative onClick={handleClose}>
          Later
        </Button>
        <Button positive onClick={handleUpdate}>
          Update Now
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default VersionUpdateNotifier;
